/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import AnimationWrapper from '../../../components/animation/animationWrapper';
import Button from '../../../components/button';
import ConfirmationModal from '../../../components/modal/confirmationModal';
import ToastComponent from '../../../components/toast';
import Heading from '../../../components/typography';
import InitialValuesForm from '../../../constants/initialValues';
import { SystemAlertConfigurationHeader } from '../../../constants/table-headers/systemAlertConfigurationHeader';
import {
  useBoolean,
  useGetSystemAlertConfigurationList,
  useWealthlaneIdentityApi,
} from '../../../hooks';
import {
  SystemAlertConfigurationService,
  type Wealthlane_Dtos_SystemAlertConfiguration_SystemAlertConfigurationDto,
} from '../../../services/wealthlane-identity-services';
import { type ModalStatusType } from '../../../types/common';
import { PageHeader } from '../company-profile/profile.styled';
import SystemAlertConfigurationModal from './systemAlertConfigurationModal';
import {
  DynamicTable,
  withDynamicTableContext,
  useDynamicTable,
} from '@wealthlane/table-builder';
import { axiosInstance } from '../../../services/apiInterceptor';
import localSchema from './schema.json';

const systemAlertConfigurationState = {
  data: InitialValuesForm.systemAlertConfigurationForm,
  id: '',
  modalStatus: 'Add' as ModalStatusType,
  loading: false,
};

const SystemAlertConfigurationComponent = (): JSX.Element => {
  const [alertInfo, setAlertInfo] = useState(systemAlertConfigurationState);
  const { tableInfo, setTableInfo } = useDynamicTable();

  useEffect(() => {
    setTableInfo(InitialValuesForm.tableParams);
  }, []);

  const { value: isModalOpen, setFalse: closeModal, setTrue: openModal } = useBoolean(false);
  const {
    value: isConfirmModalOpen,
    setFalse: closeConfirmModal,
    setTrue: openConfirmModal,
  } = useBoolean(false);

  const queryClient = useQueryClient();
  const { handleRequest } = useWealthlaneIdentityApi();
  const {
    data: alertList,
    isLoading,
    status,
  } = useGetSystemAlertConfigurationList(handleRequest, tableInfo);
  const data = React.useMemo(() => alertList?.items || [], [alertList, isLoading]);

  const tableActions = [
    {
      action: 'View',
      onClick: (id: string) => {
        getAlertData(id, 'View');
      },
      id: 'viewSystemAlertAction',
    },
    {
      action: 'Edit',
      onClick: (id: string) => {
        getAlertData(id, 'Edit');
      },
      id: 'editSystemAlertAction',
    },
    {
      action: <div className='delete-label'>Delete</div>,
      loading: alertInfo.loading,
      onClick: (id: string) => {
        setAlertInfo({
          ...alertInfo,
          id,
        });
        openConfirmModal();
      },
      id: 'deleteSystemAlertAction',
    },
  ];

  const getAlertData = (id: string, type: ModalStatusType): void => {
    setAlertInfo({ ...alertInfo, loading: true });
    openModal();
    handleRequest(
      SystemAlertConfigurationService.getApiIdentityApiAppSystemAlertConfiguration(id)
    )
      .then((data?: Wealthlane_Dtos_SystemAlertConfiguration_SystemAlertConfigurationDto) => {
        if (data != null) {
          setAlertInfo({
            ...alertInfo,
            modalStatus: type,
            id,
            data: {
              name: data.name ?? '',
              email: data.email ?? '',
              isActive: data.isActive ?? true,
            },
            loading: false,
          });
        }
      })
      .catch(() => {
        setAlertInfo({ ...alertInfo, loading: false });
      });
  };

  const deleteAlertData = (): void => {
    setAlertInfo({ ...alertInfo, loading: true });
    handleRequest(
      SystemAlertConfigurationService.deleteApiIdentityApiAppSystemAlertConfiguration(alertInfo.id)
    )
      .then(() => {
        setAlertInfo({ ...alertInfo, loading: false });
        toast.success(
          <ToastComponent label='Success' message='Alert Recipient Deleted.' />
        );
        void queryClient.invalidateQueries('getSystemAlertConfigurationList');
      })
      .catch(() => {})
      .finally(() => {
        setAlertInfo({ ...alertInfo, loading: false });
        closeConfirmModal();
      });
  };

  const schema = React.useMemo(
    () => ({
      columns: SystemAlertConfigurationHeader({
        actions: localSchema?.columns,
        tableActions,
      }),
      options: localSchema?.options as any,
    }),
    [localSchema]
  );

  const renderRightToolbarButton = () => {
    return (
      <Button
        variant={'primary'}
        title={'Add Alert Recipient'}
        icon={'add_circle_outline'}
        id='addSystemAlertConfigurationButton'
        onClick={() => {
          setAlertInfo({
            ...alertInfo,
            data: InitialValuesForm.systemAlertConfigurationForm,
            modalStatus: 'Add',
            id: '',
          });
          openModal();
        }}
      />
    );
  };

  return (
    <>
      <PageHeader data-page='systemAlertConfiguration' id='pageContainer'>
        <Heading variant='h3' title='System Alert Configuration' id='pageTitle' />
        <Heading
          variant='body-s'
          title='Manage alert recipients for system notifications'
          id='pageDescription'
        />
      </PageHeader>
      <div className='grid sm:grid-cols-12 justify-between gap-4 sm:gap-0'>
        <AnimationWrapper>
          {isModalOpen && (
            <SystemAlertConfigurationModal
              id='systemAlertConfigurationModal'
              isModalOpen={isModalOpen}
              closeModal={closeModal}
              initialData={alertInfo}
              handleRequest={handleRequest}
              setModalStatus={(value) => {
                setAlertInfo({
                  ...alertInfo,
                  modalStatus: value,
                });
              }}
              loading={alertInfo.loading}
            />
          )}

          {isConfirmModalOpen && (
            <ConfirmationModal
              isShow={isConfirmModalOpen}
              handleClose={() => {
                closeConfirmModal();
              }}
              title='Are you sure you want to delete this alert recipient?'
              handleSubmit={() => {
                deleteAlertData();
              }}
              loading={alertInfo.loading}
            />
          )}
        </AnimationWrapper>
      </div>

      <DynamicTable
        id='systemAlertConfigurationTable'
        schema={schema}
        data={data}
        totalCounts={alertList?.totalCount ?? 10}
        loading={status}
        components={{
          renderRightToolbarButton,
        }}
      />
    </>
  );
};

export default withDynamicTableContext(SystemAlertConfigurationComponent, false, axiosInstance);
